import React, { useEffect, useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useIntl } from 'react-intl'
import { useNavigate, useLocation } from 'react-router-dom'
import { Box, Tooltip, Typography } from '@mui/material'
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive'
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone'
import AssignmentOutlined from '@mui/icons-material/AssignmentOutlined'
import AssignmentLate from '@mui/icons-material/AssignmentLate'
import StarBorderIcon from '@mui/icons-material/StarBorder'
import StarIcon from '@mui/icons-material/Star'
import { parse, stringify } from 'qs'

import { TableActions } from '../table-actions'
import { TableUrlPagination } from '../../../../ui'
import { setSelectedApps, resetSelectedApps } from '../../slices'
import { applicationDataPreparation } from '../../logics'
import { useApplicationUserPermitsContext } from '../../../../pages/applications/contexts'
import { useApplicationsCalls } from '../../hooks'
import { DEFAULT_QUERY } from '../../constants'

const getLookup = (combo) => {
  let lookup = {}
  combo.forEach((item) => (lookup[item.key] = item.value))
  return lookup
}

export const AppsTable = () => {
  const intl = useIntl()
  const { formatMessage } = intl
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const {
    data,
    search,
    pagination: { total_solicitudes: totalEntries },
    isLoading: loading,
  } = useSelector((state) => state.applications)
  const { columns, tableOptions, goesToDetail } = useApplicationUserPermitsContext()
  const combos = useSelector((state) => state.combos)
  const { getApplications } = useApplicationsCalls()

  useEffect(() => {
    dispatch(resetSelectedApps())
  }, [dispatch])

  const handleSelectRows = useCallback((rows) => dispatch(setSelectedApps(rows)), [dispatch])

  useEffect(() => {
    if (location.state?.cleanup) {
      dispatch(resetSelectedApps())
    }
    const searchString = location.search.split('?')[1]
    if (searchString) {
      const urlSearchParams = parse(searchString)
      const searchParams = {
        ...DEFAULT_QUERY,
        ...urlSearchParams,
      }
      getApplications(searchParams)
    } else {
      navigate(`/applications?${stringify(DEFAULT_QUERY)}`)
    }
  }, [location.search, location.state, dispatch, getApplications, navigate])

  const handleGoToDetail = useCallback(
    (e, row) => {
      navigate(`/applications/${row['id']}${location.search}`, {
        state: { prevSearch: location.search },
      })
    },
    [navigate, location.search]
  )

  const formattedColumns = useMemo(() => {
    return columns.map((column) => {
      const baseColumn = {
        accessorKey: column.field,
        header: formatMessage({ id: `pages.applications.table.columns.${column.field}.title` }),
        size: column.width,
        minSize: column.width,
        maxSize: column.width,
        enableSorting: column.sorting !== false,
        muiTableBodyCellProps: {
          sx: {
            width: `${column.width}px`,
            minWidth: `${column.width}px`,
            maxWidth: `${column.width}px`,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          },
        },
        muiTableHeadCellProps: {
          sx: {
            width: `${column.width}px`,
            minWidth: `${column.width}px`,
            maxWidth: `${column.width}px`,
            whiteSpace: 'normal !important',
            wordWrap: 'break-word',
            overflow: 'visible !important',
            textOverflow: 'clip !important',
          },
        },
      }

      if (column.field === 'comunicaciones_pendientes') {
        return {
          ...baseColumn,
          header: 'Comunicaciones',
          muiTableHeadCellProps: {
            align: 'center',
            sx: {
              ...baseColumn.muiTableHeadCellProps.sx,
              lineHeight: 0,
              '&&': {
                paddingLeft: 0,
                paddingRight: 0,
              },
              '&.MuiTableCell-head': {
                paddingLeft: 0,
                paddingRight: 0,
              },
              '& .MuiTableSortLabel-root': {
                width: 0,
                padding: 0,
                margin: 0,
                visibility: 'hidden',
              },
              '.MuiTable-root &.MuiTableCell-head': {
                paddingLeft: 0,
              },
              '& > div, & div[class*="css-"]': {
                paddingLeft: 0,
              },
            },
          },
          Header: () => (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Tooltip
                placement="top"
                title={
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                    Comunicaciones
                  </Typography>
                }
              >
                <NotificationsNoneIcon sx={{ width: '1em', height: '1em' }} />
              </Tooltip>
            </Box>
          ),
          Cell: ({ row }) => {
            if (row.original.comunicaciones_pendientes > 0) {
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <NotificationsActiveIcon color="secondary" sx={{ fontSize: 22 }} />
                </Box>
              )
            }
            return null
          },
        }
      }

      if (column.field === 'valoraciones_pendientes') {
        return {
          ...baseColumn,
          header: 'Valoraciones',
          muiTableHeadCellProps: {
            align: 'center',
            sx: {
              ...baseColumn.muiTableHeadCellProps.sx,
              lineHeight: 0,
              '&&': {
                paddingLeft: 0,
                paddingRight: 0,
              },
              '&.MuiTableCell-head': {
                paddingLeft: 0,
                paddingRight: 0,
              },
              '& .MuiTableSortLabel-root': {
                width: 0,
                padding: 0,
                margin: 0,
                visibility: 'hidden',
              },
              '.MuiTable-root &.MuiTableCell-head': {
                paddingLeft: 0,
              },
              '& > div, & div[class*="css-"]': {
                paddingLeft: 0,
              },
            },
          },
          Header: () => (
            <Tooltip
              placement="top"
              title={
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                  Valoraciones
                </Typography>
              }
            >
              <StarBorderIcon sx={{ width: '1em', height: '1em' }} />
            </Tooltip>
          ),
          Cell: ({ row }) => {
            if (row.original.valoraciones_pendientes > 0) {
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <StarIcon color="secondary" sx={{ fontSize: 22 }} />
                </Box>
              )
            }
            return null
          },
        }
      }

      if (column.field === 'pendiente_peticionario') {
        return {
          ...baseColumn,
          header: 'Tareas',
          muiTableHeadCellProps: {
            align: 'center',
            sx: {
              ...baseColumn.muiTableHeadCellProps.sx,
              lineHeight: 0,
              '&&': {
                paddingLeft: 0,
                paddingRight: 0,
              },
              '&.MuiTableCell-head': {
                paddingLeft: 0,
                paddingRight: 0,
              },
              '& .MuiTableSortLabel-root': {
                width: 0,
                padding: 0,
                margin: 0,
                visibility: 'hidden',
              },
              '.MuiTable-root &.MuiTableCell-head': {
                paddingLeft: 0,
              },
              '& > div, & div[class*="css-"]': {
                paddingLeft: 0,
              },
            },
          },
          Header: () => (
            <Tooltip
              placement="top"
              title={
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                  Tareas
                </Typography>
              }
            >
              <AssignmentOutlined sx={{ width: '1em', height: '1em' }} />
            </Tooltip>
          ),
          Cell: ({ row }) => {
            if (row.original.pendiente_peticionario > 0) {
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <AssignmentLate color="secondary" sx={{ fontSize: 22 }} />
                </Box>
              )
            }
            return null
          },
        }
      }

      if (column.field === 'actions') {
        return {
          ...baseColumn,
          header: formatMessage({ id: 'global.actions' }),
          Cell: ({ row, cell, ...props }) => (
            <TableActions rowData={row.original} row={row} cell={cell} {...props} />
          ),
          muiTableBodyCellProps: {
            align: 'center',
            sx: {
              ...baseColumn.muiTableBodyCellProps.sx,
            },
          },
          muiTableHeadCellProps: {
            align: 'center',
            sx: {
              ...baseColumn.muiTableHeadCellProps.sx,
              height: '56px',
              padding: '8px 16px',
              verticalAlign: 'middle',
            },
          },
        }
      }

      if (column.comboId && combos[column.comboId]?.data) {
        const lookup = getLookup(combos[column.comboId].data)
        return {
          ...baseColumn,
          Cell: ({ cell }) => {
            const value = cell.getValue()
            return <span>{lookup[value] ?? value ?? '--'}</span>
          },
        }
      }

      return {
        ...baseColumn,
        Cell: ({ cell }) => <span>{cell.getValue() ?? '--'}</span>,
      }
    })
  }, [columns, combos, formatMessage])

  const processedData = useMemo(
    () => applicationDataPreparation(data, { intl }, columns),
    [data, intl, columns]
  )

  const finalTableOptions = useMemo(
    () => ({
      ...tableOptions,
      selection: true,
      tableLayout: 'auto',
      draggable: false,
    }),
    [tableOptions]
  )

  const muiTableBodyRowProps = useMemo(() => {
    if (tableOptions.rowStyle && typeof tableOptions.rowStyle === 'function') {
      return ({ row }) => {
        const style = tableOptions.rowStyle(row.original)
        return {
          sx: {
            ...style,
            '& td': {
              backgroundColor: style.backgroundColor || 'inherit',
            },
          },
        }
      }
    }
    return undefined
  }, [tableOptions])

  return (
    <TableUrlPagination
      columns={formattedColumns}
      options={finalTableOptions}
      totalEntries={totalEntries}
      search={search}
      data={processedData}
      isLoading={loading}
      onSelectionChange={handleSelectRows}
      onRowClick={goesToDetail ? handleGoToDetail : undefined}
      muiTableBodyRowProps={muiTableBodyRowProps}
    />
  )
}
